<template>
  <div class="forums-container">
    <b-row class="forums forums-row m-0">
      <!-- FILTERS COLUMN -->
      <b-col 
        v-show="hasClassifiers" 
        :lg="hasClassifiers ? 2 : 0"
        class="forums__filters pl-0"
      >
        <!-- Filter2 -->
        <Filter2 
          :classifiers-tree="classifiersTree"
          :current-locale="currentLocale"
          :selected-classifiers-for-filter.sync="selectedClassifiersForFilter"
          :filter-logic="filterLogic"
          :are-filters-visible="areFiltersVisible"
          :is-loading-filters="isLoadingFilters"
          @toggle-filters="toggleFilters"
          @set-selected-final-filter="setSelectedFinalFilter"
          @search="handleChanger(true)"
        />
      </b-col>

      <!-- FORUMS CONTENT -->
      <b-col
        :lg="hasClassifiers ? 10 : 12"
        class="forums__content"
      >
        <b-card>
          <!-- Forum app header -->
          <div class="w-100 my-1 mb-2 float-left pl-1">
            <div class="d-flex align-items-center float-left">
              <!-- Forum app name -->
              <h3 class="m-0 font-weight-600">
                {{ appTitle || $t("Forum") }}
              </h3>
            </div>
            
            <!-- Toggle filters button -->
            <div
              v-if="classifiersTree.length > 0"
              id="toggle-filter-button"
              class="text-primary ml-1 pointer position-relative float-right"
              @click="canFilter"
            >
              <b-button variant="link" class="p-0 toggle-filters-button">
                <b-icon-sliders />
              </b-button>
            </div>

            <!-- Create new forum thread -->
            <b-button
              v-if="canAdd || isStaff"
              v-b-modal.modal-create-forums
              class="float-right mr-1"
              variant="primary"
            >
              {{ $t("forums.create-title") }}
            </b-button>
          </div>

          <b-card-body class="p-1 forums__content-body">
            <!-- Loading -->
            <div v-if="isLoading">
              <div class="loading-c d-block mx-auto mt-3" />
            </div>

            <!-- Container with data -->
            <div 
              v-else-if="items.length > 0" 
              class="cards-container"
              style="flex: 1; display: flex; flex-direction: column; min-height: 0;"
            >
              <div
                id="scrollContainer"
                ref="scrollContainer"
                class="questions-container pb-2 scrollContainerClass"
                style="flex: 1; overflow-y: auto; min-height: 0;"
              >
                <!-- Items loop -->
                <div
                  v-for="(item, index) in items"
                  :key="index"
                  class="x-scroll-element mx-0"
                >
                  <b-card 
                    class="card-question"
                    no-body
                  >
                    <b-link :to="{ name: 'forum', params: { id: item.key } }">
                      <!-- Right: Main content -->
                      <div class="pb-1 d-flex align-items-center">
                        <div class="w-100">
                          <!-- First row of forum thread -->
                          <div class="text-secondary pt-1 font-weight-normal">
                            <!-- Author -->
                            <span v-if="getAuthor(item)">
                              <b-avatar
                                size="38"
                                :src="getImageSrc(getAuthor(item).avatarURL || getAuthor(item).avatar_url)"
                                :alt="getAuthor(item).name"
                                class="user-avatar mr-50"
                              />
                              {{ $t("common.created-by") }}
                              {{ getAuthor(item).name }}
                              {{ getAuthor(item).surname }}
                            </span>
                            <!-- Status Badge -->
                            <b-badge
                                v-if="showStatusBadge && item.status"
                                class="float-right mt-50"
                                :variant="getBadgeVariant(item.status)"
                              >
                                {{ getBadgeText(item.status) }}
                              </b-badge>
                          </div>

                          <!-- Title -->
                          <h4 class="font-weight-extrabold mt-1 mb-50">
                            {{
                              item.name[currentLocale] ||
                                Object.values(item.name)[0]
                            }}
                          </h4>

                          <!-- Description -->
                          <p
                            v-if="item.description"
                            class="mb-1 html-text-ellipsis-3 text-secondary content-container"
                            v-html="
                              item.description[currentLocale] ||
                                Object.values(item.description)[0]
                            "
                          />

                          <!-- Classifiers -->
                          <div
                            v-if="showClassifiers(item)"
                            id="forum-thread-classifiers" 
                            class=""
                          >
                            <b-badge
                              v-for="(classifier, index) in item.classifiers"
                              :key="index"
                              class="my-50 mr-50"
                              variant="primary"
                            >
                              {{ translate(classifier.name) }}
                            </b-badge>
                          </div>

                          <!-- Actions -->
                          <b-card-text class="w-100 mt-2 text-muted d-flex align-items-center">
                            <!-- Like forum question -->
                            <LikeButton
                              :liked="item && item.likedByMember"
                              :count="item ? (item.totalLikes || 0) : 0"
                              :size="28"
                              item-type="forums"
                              morph-type="forum"
                              :item-key="item.key"
                              stored-key="forum"
                              @toggled="fetchData"
                            />

                            <!-- Number of answers -->
                            <span class="mx-1">
                              <feather-icon
                                role="button"
                                size="20"
                                icon="MessageSquareIcon"
                                class="mr-25"
                              />
                              {{ item.answerCount || 0 }}
                            </span>

                            <!-- Timestamp -->
                            <span>
                              <feather-icon
                                size="20"
                                icon="ClockIcon"
                                class="mr-25"
                              />
                              {{ formatExactDateTime(item.createdAt) }}
                            </span>
                          </b-card-text>

                          <!-- Forum moderation section -->
                          <moderation-section
                            :content="item"
                            morph-type="forums"
                            item-type="forums"
                            item-type-name="forum"
                            :parent-key="null"
                            :can-moderate="canModerate"
                            @moderation-complete="handleModerationComplete"
                          />
                        </div>
                      </div>
                    </b-link>
                  </b-card>
                </div>

                <!-- Sentinel for viewport-based infinite scroll -->
                <div ref="loadMoreSentinel" class="load-more-sentinel" />

                <!-- Next page -->
                <b-spinner
                  v-if="isLoadingNextPage"
                  type="grow"
                  small
                  class="mx-auto my-2 d-block"
                />
                <b-button
                  v-else-if="hasMore"
                  class="d-block text-center m-auto"
                  variant="link"
                  :disabled="isLoadingNextPage"
                  @click="handleLoadOfNewItems"
                >
                  {{ $t("action.load-more") }}
                </b-button>
              </div>

              <button
                v-show="showScrollTopButton"
                type="button"
                class="scroll-top-btn"
                @click="scrollToTop"
                aria-label="Subir al inicio"
              >
                <feather-icon icon="ChevronUpIcon" size="18" />
              </button>
            </div>

            <!-- Container without data: Placeholder -->
            <b-row v-else class="horizontal-placeholder">
              <b-col cols="12">
                <img :src="placeholder">
              </b-col>
              <b-col cols="12">
                <p class="text-primary">
                  {{ $t("available.message", { itemName: "Forum" }) }}
                </p>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </b-col>
    </b-row>

    <!-- Create Forum Modal -->
    <ForumCreateEditModal
      :forum="forumToEdit"
    />
    
    <!-- Loading modal -->
    <loading-modal 
      v-model="isDownloading" 
      v-ripple.400="'rgba(234, 84, 85, 0.15)'" 
    />

  </div>
</template>

<script>
import ProductsPlaceholder from '@/assets/images/placeholders/light/placeholder-dark.jpeg';
import { getImageResource } from '@/@core/utils/image-utils';
import FileTypes from '@/@core/constants/FileTypes';
import { translateTranslationTable } from '@/@core/libs/i18n/utils';
import ForumPlaceholder from '@/assets/images/placeholders/light/forums.svg';
import FileUpload from '@core/components/files/FileUpload.vue';
import { checkPermissions } from '@/@core/utils/roles-utils';
import Ripple from 'vue-ripple-directive';
import LoadingModal from '@core/components/modal/LoadingModal.vue';
import ForumCreateEditModal from './ForumCreateEditModal.vue';
import LikeButton from '@/components/LikeButton.vue';
import Filter2 from '@/components/filters/Filter.vue';
import ModerationSection from '@/@core/components/moderation/ModerationSection.vue';
import ToastNotificationsMixin from '@core/mixins/toast-notifications/ToastNotificationsMixin';
import { formatExactDateTime } from '@/utils/dateFormatter';


export default {
  name: 'ForumsList',
  components: {
    FileUpload,
    LoadingModal,
    ForumCreateEditModal,
    LikeButton,
    Filter2,
    ModerationSection,
  },
  directives: {
    Ripple,
  },
  mixins: [ToastNotificationsMixin],
  data() {
    return {
      isLoadingNextPage: false,
      isScrollAttached: false,
      attachedScrollEl: null,
      scrollDebounceId: null,
      showScrollTopButton: false,
      lastScrollTop: 0,
      lastLoadedPage: 1,
      loadingNext: false,
      loadMoreObserver: null,
      form: {},
      imageChallenge: null,
      imageSrc: null,
      isLoading: false,
      isExportModalOpened: false,
      isDownloading: false,
      forumToEdit: null,
      // Filters data
      filterLogic: { name: this.$t("AND"), value: "AND" },
      areFiltersVisible: true,
      selectedClassifiersForFilter: {},
      selectedFilteringClassifiers: [],
      showFilter: true,
      isLoadingFilters: true,
    };
  },
  computed: {
    appId() {
      return 28;
    },
    app() {
      return this.$store.getters.apps.apps[this.appId];
    },
    appTitle() {
      return translateTranslationTable(
        this.$store.state.locale.currentLocale,
        this.app?.customizationName,
      );
    },
    FileTypes() {
      return FileTypes;
    },
    itemsData() {
      return this.$store.getters.forums.list;
    },
    items() {
      return this.itemsData?.unpaginated || [];
    },
    hasMore() {
      try {
        return (this.items?.length || 0) < (this.itemsData?.meta?.total || 0);
      } catch (e) {
        return false;
      }
    },
    currentLocale() {
      return this.$store.state.locale.currentLocale;
    },
    ProductsPlaceholder() {
      return ProductsPlaceholder;
    },
    placeholder() {
      return ForumPlaceholder;
    },
    isStaff() {
      return this.collective.isStaff;
    },
    collective() {
      return this.$store.state.collective.collective;
    },
    loggedUser() {
      return this.$store.getters.loggedUser;
    },
    loggedMemberRoles() {
      return this.$store.getters.loggedMemberRoles;
    },
    canAdd() {
      if(this.isNexos){
        return true;
      }
      // Check if it is a section
      let spaceToCheck = this.collective;
      if(this.collective.type === 'section'){
        console.log("This space is a section, using parent");
        spaceToCheck = this.collective.parent;
        //Also check if parent is a section
        if(spaceToCheck.type === 'section'){
          console.log("Parent is also a section, using parent of parent");
          console.log("parent", spaceToCheck);
          if(spaceToCheck.parent){
            spaceToCheck = spaceToCheck.parent;
          }else {
            console.log("We don't have access to parent of parent... returning false");
            return false;
          }
        }
      }
      console.log("spaceToCheck", spaceToCheck);
      return checkPermissions(
        'create',
        'forum',
        this.loggedMemberRoles,
        spaceToCheck,
      );
    },
    canManageForums() {
      return checkPermissions(
          'manage',   
          'forum',
          this.loggedMemberRoles,
          this.$store.getters.currentCollective,
        )
    },
    canModerate() {
      return this.isStaff || this.canManageForums;
    },
    isNexos() {
      return process.env.VUE_APP_CUSTOMER === 'nexos';
    },
    showStatusBadge() {
      return !this.isNexos;
    },
    // Filters computed
    classifiersTree() {
      console.log('classifiersTree computed called');
      console.log('this.$store.getters.classifiersTree:', this.$store.getters.classifiersTree);
      console.log('this.$store.getters.classifiersTree.forum:', this.$store.getters.classifiersTree?.forum);
      
      // Force reactivity by accessing the store state directly
      const storeState = this.$store.state.copernicData.classifiersTree;
      console.log('Direct store state:', storeState);
      console.log('Direct store state.forum:', storeState?.forum);
      
      if (storeState?.forum) {
        console.log('storeState.forum structure:', storeState.forum);
        console.log('storeState.forum.unpaginated:', storeState.forum.unpaginated);
        console.log('storeState.forum.data:', storeState.forum.data);
        
        // Check all possible data structures
        if (storeState.forum.unpaginated && Array.isArray(storeState.forum.unpaginated)) {
          console.log('Using unpaginated data');
          return storeState.forum.unpaginated;
        } else if (storeState.forum.data && Array.isArray(storeState.forum.data)) {
          console.log('Using data property');
          return storeState.forum.data;
        } else if (Array.isArray(storeState.forum)) {
          console.log('Using direct array');
          return storeState.forum;
        }
      }
      console.log('Returning empty array');
      return [];
    },
    hasClassifiers() {
      return this.classifiersTree.length > 0;
    },
    showFilterColumn() {
      // Show filter column if user is staff and filter should be shown
      // Don't require classifiers to be loaded yet - the Filter component will handle empty state
      return this.isStaff || this.showFilter;
    },

  },
  watch: {
    imageChallenge(newValue, oldValue) {
      if (newValue !== oldValue) {
        if (newValue) {
          this.base64Encode(newValue)
            .then((value) => {
              this.imageSrc = value;
            })
            .catch(() => {
              this.imageSrc = null;
            });
        } else {
          this.imageSrc = null;
        }
      }
    },
    classifiersTree: {
      handler(newValue) {
        if (newValue && newValue.length > 0) {
          this.showFilter = true;
        }
      },
      immediate: true
    },
    selectedClassifiersForFilter: {
      handler(newValue) {
        console.log('selectedClassifiersForFilter changed:', newValue);
        console.log('selectedClassifiersForFilter keys:', Object.keys(newValue));
        console.log('selectedClassifiersForFilter values:', Object.values(newValue));
      },
      immediate: true,
      deep: true
    }
  },
  // Fetch data when component is created!
  mounted() {
    this.attachScrollListener();
    this.setupLoadMoreObserver();
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    }
  },
  beforeDestroy() {
    this.detachScrollListener();
    this.teardownLoadMoreObserver();
    if (typeof window !== 'undefined') {
      window.removeEventListener('scroll', this.onWindowScroll);
    }
  },
  async created() {
    this.updateBreadcrumbs();
    await this.fetchData();
    console.log('Before fetchClassifiersTree, classifiersTree:', this.classifiersTree);
    await this.fetchClassifiersTree(true);
    console.log('After fetchClassifiersTree, classifiersTree:', this.classifiersTree);
    // Ensure filter is shown by default when there are classifiers
    if (this.classifiersTree.length > 0) {
      this.showFilter = true;
    }
    /* this.isLoading = true;
    this.isLoading = false; */
  },
  methods: {
    getAuthor(item) {
      const owned = item?.ownedByMember;
      const ownedMember = Array.isArray(owned) ? owned[0] : owned;

      return (
        ownedMember
        || item?.createdByCommunityUser
        || item?.createdByUser
        || null
      );
    },
    async exportCsv() {
      this.isDownloading = true;
      const response = await this.$store.dispatch("exportFiles", {
        itemType: "forums/export",
        requestConfig: {},
      });
      setTimeout(this.csv(response), 10000);
      this.isDownloading = false;
    },
    csv(response) {
      const hiddenElement = document.createElement("a");
      hiddenElement.href = response.data.file;
      hiddenElement.target = "_blank";
      hiddenElement.download = "Forums.csv";
      hiddenElement.click();
    },
    updateBreadcrumbs() {
      this.$store.commit('app/SET_BREADCRUMBS', null);
    },
    async handleLoadOfNewItems() {
      const { total } = this.itemsData.meta || {};
      if (total == null || this.items.length >= total || this.isLoadingNextPage) {
        return;
      }
      this.lastLoadedPage += 1;
      this.isLoadingNextPage = true;
      try {
        await this.fetchData();
      } finally {
        this.isLoadingNextPage = false;
        // Re-observe after the loading flag clears so a still-visible sentinel
        // does not immediately re-trigger while isLoadingNextPage is true.
        this.observeLoadMoreSentinel();
      }
    },
    attachScrollListener() {
      this.$nextTick(() => {
        const el = this.$refs.scrollContainer;
        if (!el || !el.addEventListener) {
          return;
        }
        // Re-attach when the list remounts after a filter/reset (v-if swaps the node).
        if (this.isScrollAttached && this.attachedScrollEl === el) {
          return;
        }
        this.detachScrollListener();
        el.addEventListener('scroll', this.onScroll, { passive: true });
        this.attachedScrollEl = el;
        this.isScrollAttached = true;
      });
    },
    detachScrollListener() {
      const el = this.attachedScrollEl || this.$refs.scrollContainer;
      if (el && this.isScrollAttached) {
        el.removeEventListener('scroll', this.onScroll);
      }
      this.isScrollAttached = false;
      this.attachedScrollEl = null;
      if (this.scrollDebounceId) {
        clearTimeout(this.scrollDebounceId);
        this.scrollDebounceId = null;
      }
    },
    onScroll() {
      if (this.scrollDebounceId) return;
      this.scrollDebounceId = setTimeout(() => {
        this.scrollDebounceId = null;
        const el = this.$refs.scrollContainer;
        if (!el) return;
        const threshold = 200;
        this.showScrollTopButton = el.scrollTop > threshold;
      }, 120);
    },
    setupLoadMoreObserver() {
      if (typeof IntersectionObserver === 'undefined') {
        return;
      }
      this.teardownLoadMoreObserver();
      this.loadMoreObserver = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (entry && entry.isIntersecting && this.hasMore && !this.isLoadingNextPage && !this.isLoading) {
            this.handleLoadOfNewItems();
          }
        },
        { root: null, rootMargin: '200px', threshold: 0 },
      );
      this.observeLoadMoreSentinel();
    },
    observeLoadMoreSentinel() {
      this.$nextTick(() => {
        const sentinel = this.$refs.loadMoreSentinel;
        if (!this.loadMoreObserver || !sentinel) {
          return;
        }
        this.loadMoreObserver.disconnect();
        this.loadMoreObserver.observe(sentinel);
      });
    },
    teardownLoadMoreObserver() {
      if (this.loadMoreObserver) {
        this.loadMoreObserver.disconnect();
        this.loadMoreObserver = null;
      }
    },
    onWindowScroll() {
      const threshold = 200;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      this.showScrollTopButton = scrollTop > threshold;
    },
    scrollToTop() {
      const el = this.$refs.scrollContainer;
      if (el && (el.scrollHeight > el.clientHeight) && el.scrollTop > 0) {
        try {
          el.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          el.scrollTop = 0;
        }
      } else {
        try {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
          document.documentElement.scrollTop = 0;
          document.body.scrollTop = 0;
        }
      }
    },
    canIndex(item) {
      // Check basic permissions first
      const hasBasicPermission = this.loggedUser?.key === item?.ownedByMember?.key
        || checkPermissions(
          'index',
          'forum',
          this.loggedMemberRoles,
          this.collective,
        );
      
      if (!hasBasicPermission) {
        return false;
      }
      
      // If user is staff, they can see all forums
      if (this.isStaff) {
        return true;
      }
      
      // For non-staff users, only hide forums that are explicitly pending moderation
      // moderationStatus: 0 = pending, 1 = approved, 2 = rejected, null = no moderation required
      const isPendingModeration = item.moderationStatus === 0;
      
      return !isPendingModeration;
    },
    formatExactDateTime(timestamp) {
      return formatExactDateTime(timestamp, this.currentLocale);
    },
    // TODO: move to utils
    base64Encode(data) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(data);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    },
    getBadgeVariant(status) {
      if (!status || status === 42658) {
        return 'warning';
      }
      if (status == 42659 || status === 42736) {
        return 'success';
      }
    },
    getBadgeText(status) {
      if (!status || status === 42658) {
        return this.$t('forums.open');
      }
      if (status == 42659 || status === 42736) {
        return this.$t('forums.closed');
      }
    },
    // Call store action (with dispach) to load data from backend
    getImageSrc(url) {
      return getImageResource(url);
    },
    async fetchData() {
      console.log('fetchData called');
      console.log('selectedFilteringClassifiers in fetchData:', this.selectedFilteringClassifiers);
      console.log('filterLogic.value in fetchData:', this.filterLogic.value);

      const page = this.lastLoadedPage;
      const isFirstPage = page === 1;
      if (isFirstPage && !this.isLoadingNextPage) {
        this.isLoading = true;
      }

      const requestConfig = {
        count: 16,
        orderByDate: 1,
        classifiers: this.selectedFilteringClassifiers,
        filterLogic: this.filterLogic.value,
      };

      if (isFirstPage) {
        // Reset list on first page (filters / initial load / moderation refresh).
        await this.$store.dispatch('getItems', {
          itemType: 'forums',
          storedKey: 'list',
          forceAPICall: true,
          page,
          requestConfig,
        });
      } else {
        // Append next pages: fetch without writing, then commit so SET_ITEMS appends.
        const response = await this.$store.dispatch('getItems', {
          itemType: 'forums',
          storedKey: 'list',
          forceAPICall: true,
          noSet: true,
          page,
          requestConfig,
        });
        if (response?.meta) {
          this.$store.commit('SET_ITEMS', {
            type: 'forums',
            storedKey: 'list',
            data: response,
          });
        }
      }

      this.updateBreadcrumbs();
      this.$nextTick(() => {
        this.attachScrollListener();
        // First-page loads need the sentinel observed once the list is rendered.
        // Next-page loads re-observe from handleLoadOfNewItems after the loading flag clears.
        if (isFirstPage) {
          this.observeLoadMoreSentinel();
        }
      });
      if (isFirstPage) {
        this.isLoading = false;
      }
    },
    async toggleLike(key) {
      // TODO: Check with BE itemTypes and morphTypes
      await this.$store.dispatch('toggleLike', {
        itemType: 'forums',
        morphType: 'forum',
        key,
        storedKey: 'list',
      });
    },
    timestampToTime(timestamp) {
      return moment(timestamp).format('DD/MM/YYYY HH:mm:ss');
    },
    showClassifiers(item) {
      return item.classifiers && item.classifiers?.length > 0;
    },
    translate(field) {
      return translateTranslationTable(
        this.currentLocale,
        field,
      );
    },
    // Filter methods
    toggleFilters() {
      this.areFiltersVisible = !this.areFiltersVisible;
      if (!this.areFiltersVisible) {
        this.selectedFilteringClassifiers = [];
        this.selectedClassifiersForFilter = {};
        this.lastLoadedPage = 1;
        this.fetchData();
      }
    },
    setSelectedFinalFilter() {
      console.log('setSelectedFinalFilter called');
      console.log('this.selectedClassifiersForFilter:', this.selectedClassifiersForFilter);
      console.log('Object.values(this.selectedClassifiersForFilter):', Object.values(this.selectedClassifiersForFilter));
      
      this.selectedFilteringClassifiers = [];
      const values = Object.values(this.selectedClassifiersForFilter);
      console.log('Values array:', values);
      
      for(let selectedClassifier of values){
        console.log('Processing selectedClassifier:', selectedClassifier);
        if(selectedClassifier && selectedClassifier.key){
          console.log('Adding classifier key:', selectedClassifier.key);
          this.selectedFilteringClassifiers.push(selectedClassifier.key);
        }
      }
      
      // Clean up the array to remove Vue reactivity wrappers
      this.selectedFilteringClassifiers = this.selectedFilteringClassifiers.filter(item => typeof item === 'string');
      
      console.log('Final selectedFilteringClassifiers:', this.selectedFilteringClassifiers);
    },
    handleChanger(isFiltering = false) {
      console.log('handleChanger called with isFiltering:', isFiltering);
      console.log('selectedFilteringClassifiers before fetchData:', this.selectedFilteringClassifiers);
      this.lastLoadedPage = 1;
      this.fetchData();
    },
    canFilter() {
      if (this.classifiersTree.length > 0) {
        this.showFilter = !this.showFilter;
      } else {
        this.showFilter = false;
      }
    },
    async fetchClassifiersTree(force = false) {
      console.log('fetchClassifiersTree called with force:', force);
      this.isLoadingFilters = true;
      
      const result = await this.$store.dispatch("getItems", {
        itemType: "classifiersTree",
        storedKey: "forum",
        forceAPICall: force,
        requestConfig: {
          modelType: "forum",
          morphType: "forum",
          count: 1000,
        },
      });
      console.log('fetchClassifiersTree result:', result);
      console.log('fetchClassifiersTree result.data:', result.data);
      console.log('fetchClassifiersTree result.meta:', result.meta);
      
      // Add a small delay to ensure store is updated
      await this.$nextTick();
      
      console.log('After fetch, classifiersTree computed value:', this.classifiersTree);
      console.log('Store state classifiersTree:', this.$store.state.copernicData.classifiersTree);
      console.log('Store getters classifiersTree:', this.$store.getters.classifiersTree);
      
      // Set loading to false after data is loaded
      this.isLoadingFilters = false;
    },

    // Moderation methods
    async handleModerationComplete({ action, contentKey }) {
      if (action === 'reject') {
        // Remove the forum from the list
        const index = this.items.findIndex(item => item.key === contentKey);
        if (index > -1) {
          this.items.splice(index, 1);
        }
      }
      this.lastLoadedPage = 1;
      await this.fetchData();
    },
  }
};
</script>

<style>
.content-container img {
  max-width: 90%;
}
.card-question {
  padding: 1rem 2rem;
}
.forums__filters {
  display: block !important;
  visibility: visible !important;
}

/* Full height layout */
.forums-container {
  min-height: 100vh;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.forums-row {
  flex: 1;
  min-height: calc(100vh - 100px); /* Adjust based on your header height */
}

.forums__filters {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.forums__filters .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.forums__content {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.forums__content .card {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.forums__content .card-body,
.forums__content-body {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.loading-logo {
  width: 56px;
  height: 56px;
  opacity: 0.9;
  animation: loading-spin 1.1s linear infinite;
}

.loading-c {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 6px solid #f2c200;
  border-right-color: transparent;
  border-bottom-color: transparent;
  animation: loading-spin 0.9s linear infinite;
}

@keyframes loading-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.scroll-top-btn {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  z-index: 1050;
  background-color: #f2c200;
  border: none;
  color: #ffffff; 
  cursor: pointer;
  transition: transform .12s ease, box-shadow .12s ease, opacity .12s ease;
}
.scroll-top-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.22);
}
.scroll-top-btn:active {
  transform: translateY(0);
}
.scroll-top-btn:focus {
  outline: none;
}

/* TODO: fix responsive with bootstrap, better than media queries */
@media only screen and (max-width: 600px) {
  .cards-container {
    margin: 0 -2rem;
  }
  .card-question {
    padding: .6rem .75rem;
  }
}
</style>
